/**
 * StationaryTracker — tracks how long the player has been continuously still.
 *
 * Used as a gate by Earth nodes (Bedrock, Tremor Sense, Deep Root, etc.),
 * Fire's Hearth, Blades' Inevitability, and Specter's Hide/Stealth.
 * Callers pass their own threshold to isStationary() — the tracker
 * accumulates duration, consumers decide what "long enough" means.
 *
 * A dead-zone of 0.5 pixels per frame filters out physics jitter so the
 * timer doesn't reset when the player is visually stationary.
 */

export class StationaryTracker {
  /** Movement below this per-frame distance counts as stationary. */
  readonly deadZonePixels: number;

  private _stationaryMs = 0;
  private _isMoving = true;
  /** When true, update() is bypassed — stationary state is locked. Cleared by reset(). */
  private _forceLocked = false;

  constructor(deadZonePixels = 0.5) {
    this.deadZonePixels = deadZonePixels;
  }

  /**
   * Returns true if the player has been stationary for at least `thresholdMs`.
   * A threshold of 0 returns true as soon as movement stops.
   * Returns false while the player is actively moving.
   */
  isStationary(thresholdMs: number): boolean {
    if (this._isMoving) return false;
    return this._stationaryMs >= thresholdMs;
  }

  /**
   * Returns how many milliseconds the player has been continuously stationary.
   * Returns 0 if the player is currently moving.
   */
  getStationaryDurationMs(): number {
    return this._stationaryMs;
  }

  /**
   * Per-frame update. Call with the player's movement distance this frame.
   * Any movement above the dead-zone resets the stationary timer.
   *
   * @param delta Frame delta in ms
   * @param movementPixels Absolute distance moved this frame (magnitude of velocity × delta/1000)
   */
  update(delta: number, movementPixels: number): void {
    if (this._forceLocked) return;
    if (movementPixels > this.deadZonePixels) {
      this._stationaryMs = 0;
      this._isMoving = true;
    } else {
      this._stationaryMs += delta;
      this._isMoving = false;
    }
  }

  /** Reset to initial state. Called on scene shutdown to prevent leaks across run restarts. */
  reset(): void {
    this._stationaryMs = 0;
    this._isMoving = true;
    this._forceLocked = false;
  }

  /** Force stationary state with a given duration. Locks the tracker so update() is bypassed
   *  until reset() is called. Used by SimulationBridge for skills that gate on stationary time. */
  forceStationary(durationMs: number): void {
    this._stationaryMs = durationMs;
    this._isMoving = false;
    this._forceLocked = true;
  }
}
